import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, Column, DistributiveOmit, StyleProps } from "@hex-insights/core";
import { FieldIf, FieldSet, Form, FormEditButtonPair, FormState, SubmitButton } from "@hex-insights/forms";
import {
	StudentSocialEmotionalNoteCreateMutation,
	StudentSocialEmotionalNoteDetailQuery,
	StudentSocialEmotionalNoteUpdateMutation,
} from "../../../Utilities";
import { StudentSocialEmotionalNoteFormat } from "../../Format";
import { StudentSocialEmotionalNoteFormState, StudentSocialEmotionalNoteFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<StudentSocialEmotionalNoteCreateMutation["createStudentSocialEmotionalNote"]>,
	"formState" | "create"
> & {
	formStateArgs?: StudentSocialEmotionalNoteFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the StudentSocialEmotionalNote model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<StudentSocialEmotionalNoteFormValues.Create>;
	create: CreateFunction<StudentSocialEmotionalNoteFormValues.Create, T>;
	onSuccess: (formState: FormState<StudentSocialEmotionalNoteFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentSocialEmotionalNoteFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the StudentSocialEmotionalNote model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "studentSocialEmotionalNote.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<StudentSocialEmotionalNoteFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.IsIssueNatureLearningDifficulty formState={formState} />
			<Field.IsIssueNatureAttentionIssue formState={formState} />
			<Field.IsIssueNatureFocusIssue formState={formState} />
			<Field.IsIssueNatureEmotionalOrVulnerableIssue formState={formState} />
			<Field.IsIssueNatureOther formState={formState} />
			<Field.IssueNatureOther formState={formState} />
			<Field.IsSolutionRecDifferentiatedLearning formState={formState} />
			<Field.IsSolutionRecIndividualLearningPlan formState={formState} />
			<Field.IsSolutionRecTutoring formState={formState} />
			<Field.IsSolutionRecProfessionalSupport formState={formState} />
			<Field.IsSolutionRecInClassSupport formState={formState} />
			<Field.IsSolutionRecOther formState={formState} />
			<Field.SolutionRecOther formState={formState} />
			<Field.StudentNote.Select formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<StudentSocialEmotionalNoteUpdateMutation["updateStudentSocialEmotionalNote"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<
		DistributiveOmit<StudentSocialEmotionalNoteFormState.UseDetailFormStateArgs, "initialFormValues">
	>;
};

/**
 * Detail form of the StudentSocialEmotionalNote model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({
		...formStateArgs,
		studentSocialEmotionalNote: props.studentSocialEmotionalNote,
	});

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	studentSocialEmotionalNote: StudentSocialEmotionalNoteDetailQuery["studentSocialEmotionalNote"];
	formState: FormState<StudentSocialEmotionalNoteFormValues.Detail>;
	update: UpdateFunction<StudentSocialEmotionalNoteFormValues.Detail, T>;
	onSuccess: (formState: FormState<StudentSocialEmotionalNoteFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentSocialEmotionalNoteFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the StudentSocialEmotionalNote model using the given form state.
 */
export function Detail<T>({
	formState,
	studentSocialEmotionalNote,
	update,
	onSuccess,
	formNameSuffix,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName =
		`studentSocialEmotionalNote.detail.${studentSocialEmotionalNote.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} studentSocialEmotionalNote={studentSocialEmotionalNote} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<
		DistributiveOmit<StudentSocialEmotionalNoteFormState.UseReadOnlyFormStateArgs, "initialFormValues">
	>;
};

/**
 * Read-only detail form of the StudentSocialEmotionalNote model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({
		...formStateArgs,
		studentSocialEmotionalNote: props.studentSocialEmotionalNote,
	});

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	studentSocialEmotionalNote: StudentSocialEmotionalNoteDetailQuery["studentSocialEmotionalNote"];
	formState: FormState<StudentSocialEmotionalNoteFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the StudentSocialEmotionalNote model using the given form state.
 */
export function ReadOnly({ formState, studentSocialEmotionalNote, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} studentSocialEmotionalNote={studentSocialEmotionalNote} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	studentSocialEmotionalNote: StudentSocialEmotionalNoteDetailQuery["studentSocialEmotionalNote"];
	formState: FormState<StudentSocialEmotionalNoteFormValues.Detail>;
};

function DetailFields({ formState, studentSocialEmotionalNote }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.IsIssueNatureLearningDifficulty formState={formState} />
			<Field.IsIssueNatureAttentionIssue formState={formState} />
			<Field.IsIssueNatureFocusIssue formState={formState} />
			<Field.IsIssueNatureEmotionalOrVulnerableIssue formState={formState} />
			<Field.IsIssueNatureOther formState={formState} />
			<Field.IssueNatureOther formState={formState} />
			<Field.IsSolutionRecDifferentiatedLearning formState={formState} />
			<Field.IsSolutionRecIndividualLearningPlan formState={formState} />
			<Field.IsSolutionRecTutoring formState={formState} />
			<Field.IsSolutionRecProfessionalSupport formState={formState} />
			<Field.IsSolutionRecInClassSupport formState={formState} />
			<Field.IsSolutionRecOther formState={formState} />
			<Field.SolutionRecOther formState={formState} />
			<Field.StudentNote.Select formState={formState} currentInstance={studentSocialEmotionalNote.studentNote} />
		</React.Fragment>
	);
}

const issueNatureFieldNames: (keyof Omit<StudentSocialEmotionalNoteFormValues.Base, "studentNoteID">)[] = [
	"isIssueNatureLearningDifficulty",
	"isIssueNatureAttentionIssue",
	"isIssueNatureFocusIssue",
	"isIssueNatureEmotionalOrVulnerableIssue",
	"isIssueNatureOther",
	"issueNatureOther",
];

const solutionRecFieldNames: (keyof Omit<StudentSocialEmotionalNoteFormValues.Base, "studentNoteID">)[] = [
	"isSolutionRecDifferentiatedLearning",
	"isSolutionRecIndividualLearningPlan",
	"isSolutionRecTutoring",
	"isSolutionRecProfessionalSupport",
	"isSolutionRecInClassSupport",
	"isSolutionRecOther",
	"solutionRecOther",
];

export type CreateFieldsInStudentNoteProps = {
	formState: FormState<StudentSocialEmotionalNoteFormValues.CreateInStudentNote>;
};

export function CreateFieldsInStudentNote({ formState }: CreateFieldsInStudentNoteProps) {
	return (
		<React.Fragment>
			<FieldSet
				formState={formState}
				names={issueNatureFieldNames}
				label={StudentSocialEmotionalNoteFormat.fieldSetLabels.issueNature}
				style={{ width: "100%" }}
			>
				<Column
					justify="spaced-start"
					verticalSpacing="0.25rem"
					style={{ padding: "var(--radio-field__value---padding)" }}
				>
					<Field.IsIssueNatureLearningDifficulty formState={formState} style={{ width: "100%" }} />
					<Field.IsIssueNatureAttentionIssue formState={formState} style={{ width: "100%" }} />
					<Field.IsIssueNatureFocusIssue formState={formState} style={{ width: "100%" }} />
					<Field.IsIssueNatureEmotionalOrVulnerableIssue formState={formState} style={{ width: "100%" }} />
					<Field.IsIssueNatureOther formState={formState} style={{ width: "100%" }} />
					<FieldIf formState={formState} name="issueNatureOther" condition={formState.formValues.isIssueNatureOther}>
						<Field.IssueNatureOther formState={formState} style={{ width: "100%" }} />
					</FieldIf>
				</Column>
			</FieldSet>
			<FieldSet
				formState={formState}
				names={solutionRecFieldNames}
				label={StudentSocialEmotionalNoteFormat.fieldSetLabels.solutionRec}
				style={{ width: "100%" }}
			>
				<Column
					justify="spaced-start"
					verticalSpacing="0.25rem"
					style={{ padding: "var(--radio-field__value---padding)" }}
				>
					<Field.IsSolutionRecDifferentiatedLearning formState={formState} style={{ width: "100%" }} />
					<Field.IsSolutionRecIndividualLearningPlan formState={formState} style={{ width: "100%" }} />
					<Field.IsSolutionRecTutoring formState={formState} style={{ width: "100%" }} />
					<Field.IsSolutionRecProfessionalSupport formState={formState} style={{ width: "100%" }} />
					<Field.IsSolutionRecInClassSupport formState={formState} style={{ width: "100%" }} />
					<Field.IsSolutionRecOther formState={formState} style={{ width: "100%" }} />
					<FieldIf formState={formState} name="solutionRecOther" condition={formState.formValues.isSolutionRecOther}>
						<Field.SolutionRecOther formState={formState} style={{ width: "100%" }} />
					</FieldIf>
				</Column>
			</FieldSet>
		</React.Fragment>
	);
}
